/**
 * Competitor data for /compare/[slug] pages.
 *
 * IMPORTANT: All comparison data below is researched from each competitor's
 * public website + public reviews as of Feb 2026. We deliberately frame
 * "Where [X] wins" honestly so visitors trust our analysis. Pricing and
 * policies change frequently — every page renders an "as of" disclaimer
 * encouraging visitors to verify with the competitor directly.
 *
 * To add a competitor: append a new entry, run /tmp/gen_sitemap.py to
 * regenerate the sitemap, ship.
 */

export const COMPETITORS = [
  {
    slug: 'shipbob',
    name: 'ShipBob',
    tagline: 'The largest tech-forward 3PL — but its scale comes with friction',
    blurb:
      "ShipBob is the most well-known 3PL in the DTC space, with a multi-warehouse US network and an extensive Shopify integration. They're a strong fit for brands shipping 5,000+ orders/month who want geographic distribution. For smaller and mid-size operators, the platform fees, multi-tier pricing, and onboarding rigidity often outweigh the scale benefits.",
    theyWin: [
      'Multi-warehouse network: 30+ fulfillment centers nationwide',
      'Mature software platform with deep Shopify Plus integration',
      'Strong fit for brands needing enterprise-scale infrastructure',
      'B2B/wholesale fulfillment options at higher tiers',
    ],
    weWin: [
      'No platform fees, no setup fees, no monthly minimums',
      'Single transparent rate: $2 per order. Their tiered pricing requires sales conversations',
      'White-glove onboarding from a real team — no chatbot ticket queues',
      'Fast to start: most brands ship within a week of signing',
      'Operator-led support: when you message us, you get a fulfillment expert, not a Tier-1 rep',
    ],
    table: [
      { label: 'Per-order rate', shipmaster: '$2 flat', them: 'Tiered, varies by volume' },
      { label: 'Monthly minimums', shipmaster: 'None', them: 'None advertised, but platform fees apply' },
      { label: 'Setup fees', shipmaster: '$0', them: 'Onboarding fees on some plans' },
      { label: 'Platform / software fee', shipmaster: '$0', them: 'Built into per-order pricing' },
      { label: 'Receiving', shipmaster: '$15/pallet', them: 'Per-shipment, varies' },
      { label: 'Long-term storage', shipmaster: '$50/pallet/mo', them: 'Tiered, escalates after 90 days' },
      { label: 'Average onboarding time', shipmaster: '5-7 days', them: '2-4 weeks' },
      { label: 'Shopify integration', shipmaster: 'Native, real-time', them: 'Native, real-time' },
      { label: 'TikTok Shop', shipmaster: 'Native', them: 'Available' },
      { label: 'Amazon FBM/MFN', shipmaster: 'Yes', them: 'Yes' },
    ],
    switchFaqs: [
      {
        q: 'How do I move my inventory from ShipBob to Shipmaster?',
        a: "We coordinate the transfer directly with ShipBob's outbound team. You ship a pallet (or pallets) to our Weston, FL warehouse. We unload, count, photograph, and rack within 48 hours of receipt. You only pay our standard receiving fee ($15/pallet) — no migration markup.",
      },
      {
        q: 'Will I lose orders during the transition?',
        a: "No. We do a phased cutover: you stop ShipBob from receiving new POs (set minimum stock there), inventory transfers, then we go live on your Shopify integration. Most brands experience zero downtime when they coordinate the switch around a slower week.",
      },
      {
        q: 'What if I have inventory at multiple ShipBob warehouses?',
        a: "We consolidate it. You'll likely lose some West Coast same-day delivery, but most brands find that our Florida-origin lane combined with no platform/storage fees nets out cheaper for the same SLA.",
      },
      {
        q: "Why are you cheaper than ShipBob if you're smaller?",
        a: "We don't carry their overhead. No software engineering team, no multi-warehouse coordination cost, no marketing-heavy customer acquisition. We're a focused operation in one location, and we pass the savings directly into per-order pricing.",
      },
    ],
  },
  {
    slug: 'shipmonk',
    name: 'ShipMonk',
    tagline: 'Mid-size 3PL with strong subscription-box capability',
    blurb:
      "ShipMonk is a solid mid-size 3PL with particular strength in subscription-box and kitting workflows. Their pricing is competitive on paper but layers fees that add up — pick fees beyond the first item, transaction fees, monthly software charges. For straightforward Shopify and DTC operations, brands often find Shipmaster's flat $2/order math simpler and cheaper end-to-end.",
    theyWin: [
      'Specialized expertise in subscription-box assembly at scale',
      'Multi-channel inventory sync across many platforms',
      'Established brand with venture funding and stability',
      'Returns processing workflow is mature',
    ],
    weWin: [
      'Truly flat pricing — $2/order covers pick, pack, label. No per-item-after-first surcharges',
      'No mandatory monthly software/platform fees',
      "Faster, more transparent onboarding with no contract minimums",
      "When you're ready to scale, you don't get bumped to a new account team — you get the same operators",
      "We're equally strong on subscription, DTC, FBM, and TikTok Shop — no specialty lock-in",
    ],
    table: [
      { label: 'Per-order rate', shipmaster: '$2 flat (covers pick + pack + label)', them: 'Base + per-additional-item' },
      { label: 'Software / platform fee', shipmaster: '$0', them: 'Monthly fee on most plans' },
      { label: 'Receiving', shipmaster: '$15/pallet', them: 'Per-pallet + per-SKU' },
      { label: 'Long-term storage', shipmaster: '$50/pallet/mo', them: 'Tiered by space and duration' },
      { label: 'Contract', shipmaster: 'Month-to-month', them: 'Annual or quarterly common' },
      { label: 'Setup fees', shipmaster: '$0', them: 'Implementation fee on larger plans' },
      { label: 'Subscription box capability', shipmaster: 'Yes — kitting + recurring', them: 'Yes — specialty area' },
      { label: 'Onboarding', shipmaster: '5-7 days', them: '2-4 weeks' },
    ],
    switchFaqs: [
      {
        q: 'I run a subscription box. Can Shipmaster handle the assembly?',
        a: "Yes. We do kitting, BOM-based assembly, custom inserts, and recurring monthly box workflows. The difference: we don't charge a 'subscription specialty' premium. Same $2/order rate applies. We'll quote your specific kit complexity on the estimate call.",
      },
      {
        q: "Why are ShipMonk's quotes so much higher than what they advertise?",
        a: "Their advertised rate is the base pick (first item). Pick-after-first, pack materials, software fees, and storage tiers stack on top. By the time you have a 3-item bundle with a custom mailer, the effective cost is often 2x the advertised number. We saw a brand last quarter cut their effective per-order cost by 38% switching to us — same SLAs, no kitting trade-offs.",
      },
      {
        q: "Can I migrate during a contract?",
        a: "ShipMonk contracts often have 30-90 day notice clauses. We help you read the exit terms and time your migration to minimize overlap. Most brands run a parallel cutover window of 1-2 weeks to drain old inventory while we ship new orders.",
      },
    ],
  },
  {
    slug: 'red-stag',
    name: 'Red Stag Fulfillment',
    tagline: 'Heavy-and-oversized specialist with a premium price tag',
    blurb:
      "Red Stag has carved out a specialty serving brands with heavy, oversized, or high-value SKUs (think furniture, fitness equipment, fragile goods). They're genuinely good at that niche — accuracy guarantees, robust receiving, photo documentation. For typical Shopify and DTC brands shipping standard parcels, Red Stag's premium pricing is overkill. Most brands shouldn't pay heavy-3PL prices for light-3PL needs.",
    theyWin: [
      'Specialized infrastructure for heavy/oversized/fragile SKUs',
      'Industry-leading accuracy and shrinkage guarantees',
      'High-touch handling for high-value goods',
      'Strong fit for brands shipping <100 orders/day with high AOV',
    ],
    weWin: [
      'Right-sized pricing for normal parcel weights — no heavy-3PL premium for non-heavy products',
      'Better suited for high-volume DTC: 1,000-10,000+ orders/month',
      'Native Shopify, TikTok Shop, Amazon FBM workflows — Red Stag is more enterprise-focused',
      'Same care for fragile goods (we photograph receiving, document damages) without the premium',
      "$2/order vs Red Stag's tier-and-volume-priced model that often runs $4-7/order effective",
    ],
    table: [
      { label: 'Per-order rate', shipmaster: '$2 flat', them: 'Tier-based, often $4-7 effective' },
      { label: 'Best fit', shipmaster: 'Standard DTC parcel volume (1k-50k orders/mo)', them: 'Heavy/oversized/high-AOV' },
      { label: 'Monthly minimums', shipmaster: 'None', them: 'Higher minimums common' },
      { label: 'Receiving', shipmaster: '$15/pallet', them: 'Higher — premium handling included' },
      { label: 'Long-term storage', shipmaster: '$50/pallet/mo', them: 'Premium tiered' },
      { label: 'Accuracy guarantee', shipmaster: '99.9% (we own mistakes)', them: '100% guarantee with credit' },
      { label: 'Onboarding', shipmaster: '5-7 days', them: '3-6 weeks' },
    ],
    switchFaqs: [
      {
        q: "I have some heavy/fragile SKUs. Can Shipmaster still handle them?",
        a: "Yes — we handle fragile goods, kitting, and oversized items as part of our standard workflow. We won't undercut Red Stag for a brand whose entire catalog is 70-lb fitness equipment, but for typical DTC brands with mixed catalogs that include some fragile/heavy SKUs, we deliver the same care without the heavy-3PL price floor.",
      },
      {
        q: "How does your accuracy guarantee work?",
        a: "We own pick-pack errors. If we ship the wrong product or miss something, we cover the cost of the resend and the customer's reshipping. Red Stag offers a 100% accuracy credit-back guarantee that's marketing-effective; in practice the financial outcome is similar — fewer mistakes, fewer credits, same root cause.",
      },
      {
        q: "Are you as careful with documentation as Red Stag?",
        a: "We photograph all inbound receiving, log condition on every pallet, and timestamp damage notes. Brands that need that level of audit trail get it from us by default. The difference is we don't charge a premium for it.",
      },
    ],
  },
  {
    slug: 'flexport',
    name: 'Flexport',
    tagline: 'Enterprise freight-forwarder turned 3PL — built for scale, priced for it',
    blurb:
      "Flexport built its reputation on tech-enabled freight forwarding and expanded into ecommerce fulfillment after acquiring Shopify Logistics. Their stack is genuinely impressive at enterprise scale — full international freight, fulfillment, and last-mile in one platform. The trade-off: Flexport is built for brands managing complex global supply chains. For a typical Shopify or DTC brand shipping 1,000-50,000 orders per month, the platform overhead and enterprise-tier pricing rarely pencil out.",
    theyWin: [
      'Best-in-class freight forwarding and customs management for international brands',
      'Multi-warehouse fulfillment network with strong tech platform',
      'Integrated supply-chain visibility — ocean freight to last mile in one dashboard',
      'Strong fit for enterprise brands with global manufacturing and inbound complexity',
    ],
    weWin: [
      'Right-sized for DTC scale — no enterprise platform tax for SMB volume',
      "Pricing is one number ($2/order). Flexport's quotes layer freight, fulfillment, and platform fees",
      'Faster onboarding (5-7 days vs Flexport\'s typical 30-90 day implementation)',
      "You don't need supply-chain visibility software when you're talking to your account manager weekly",
      'Operator-led support: real fulfillment people, not a sales-engineering team',
    ],
    table: [
      { label: 'Per-order rate', shipmaster: '$2 flat', them: 'Custom quote — typically tiered with platform fees' },
      { label: 'Best fit', shipmaster: 'DTC: 1k-50k orders/mo', them: 'Enterprise: 25k+ orders/mo with global freight needs' },
      { label: 'Setup / implementation', shipmaster: '$0 · 5-7 days', them: '30-90 day implementation common' },
      { label: 'Platform / software fees', shipmaster: '$0', them: 'Subscription tier on top of fulfillment' },
      { label: 'Receiving', shipmaster: '$15/pallet', them: 'Custom — bundled with freight intake' },
      { label: 'Long-term storage', shipmaster: '$50/pallet/mo', them: 'Tiered, escalates after 60-90 days' },
      { label: 'International freight', shipmaster: 'Outbound only', them: 'Yes — core specialty (ocean, air, customs)' },
      { label: 'Contract', shipmaster: 'Month-to-month', them: 'Annual common at scale' },
      { label: 'Account model', shipmaster: 'Direct operator access', them: 'Sales-engineer + CSM model' },
    ],
    switchFaqs: [
      {
        q: "I'm using Flexport for inbound freight too. Can Shipmaster handle that?",
        a: "We coordinate with your existing freight forwarder for inbound — most brands keep their freight relationship and just split out fulfillment. If you want a one-stop solution, we can recommend freight partners who pair well with our intake operation, but we won't pretend to compete with Flexport's freight platform. We just stay in our lane and do fulfillment well.",
      },
      {
        q: "Why is Flexport's quote so much higher than what I expected?",
        a: "Flexport prices for the full supply-chain stack — implementation, platform access, integrations, fulfillment, plus margin on freight if you're using that side. For brands that don't need the full stack, you're paying for capabilities that sit unused. Our pricing covers fulfillment only, which is what most DTC brands actually need.",
      },
      {
        q: "How does the migration from Flexport work?",
        a: "Same as any 3PL transfer. You schedule outbound from Flexport's warehouse(s) to ours. The bigger consideration is contract — Flexport typically uses annual agreements with notice clauses. We help you read the exit terms and time the move so you're not paying both fulfillment fees in parallel for long.",
      },
    ],
  },
  {
    slug: 'ryder-ecommerce',
    name: 'Ryder Ecommerce',
    tagline: 'Enterprise logistics giant doing 3PL — bigger, slower, more expensive',
    blurb:
      "Ryder is one of the largest logistics companies in North America, and their ecommerce arm leverages that scale: dozens of warehouses, deep freight integration, automated facilities. For brands shipping 10,000+ orders per month with complex distribution requirements, Ryder is a serious option. For typical Shopify and DTC brands, Ryder operates at a scale and price point that rarely matches the actual need — you end up paying enterprise overhead for SMB throughput.",
    theyWin: [
      'Largest US warehouse network — coast-to-coast geographic distribution',
      'Deep integration with Ryder\'s commercial freight operations',
      'Automated fulfillment facilities with high-throughput pick rates',
      'Strong fit for omnichannel brands with retail + DTC + B2B mix',
    ],
    weWin: [
      'Pricing scaled for DTC, not Fortune-500 logistics — $2/order vs Ryder\'s enterprise rates',
      'Direct relationship with the people running your account — Ryder accounts often go through layered sales/CSM teams',
      'Faster decisions: when you need a custom packing tweak or a rush shipment, we move in hours, not change-request tickets',
      'No multi-million-dollar minimum-volume conversations to start',
      'Better fit for the 1,000-50,000 orders/mo range where most DTC brands actually live',
    ],
    table: [
      { label: 'Per-order rate', shipmaster: '$2 flat', them: 'Enterprise contract pricing, custom quoted' },
      { label: 'Minimum volume to engage', shipmaster: 'None — 1 order works', them: 'Significant — typically 10k+ orders/mo' },
      { label: 'Setup / onboarding', shipmaster: '5-7 days', them: '60-120 day enterprise implementation' },
      { label: 'Contract length', shipmaster: 'Month-to-month', them: 'Multi-year typical' },
      { label: 'Receiving', shipmaster: '$15/pallet', them: 'Bundled with master services agreement' },
      { label: 'Long-term storage', shipmaster: '$50/pallet/mo', them: 'Tiered enterprise pricing' },
      { label: 'Account access', shipmaster: 'Direct line to operators', them: 'Layered: CSM + ops + finance' },
      { label: 'Best for', shipmaster: 'DTC brands 1k-50k orders/mo', them: 'Enterprise omnichannel brands at scale' },
      { label: 'Software platform', shipmaster: 'Real-time Shopify sync', them: 'Ryder\'s proprietary WMS + EDI' },
    ],
    switchFaqs: [
      {
        q: "Ryder has 30+ warehouses. Won't I lose multi-warehouse coverage?",
        a: "Yes — and we're honest about that. If your business genuinely depends on multi-warehouse 2-day coverage for cost reasons, Ryder's network has real value. For most brands shipping under 25k/month from a single origin, the math works out fine: our Florida-origin lane plus blended carrier rates often produces comparable effective cost without the enterprise overhead. Run the numbers on your actual order distribution before assuming you need multiple warehouses.",
      },
      {
        q: "Why does Ryder require such a long onboarding?",
        a: "Enterprise 3PLs ramp slowly because they're integrating into complex IT environments — EDI, ERP systems, custom routing rules. That works for brands that need it. For a Shopify or TikTok Shop operator that just wants orders to ship on time with clean tracking, that level of integration is overkill. We're operational in a week with the same outcome.",
      },
      {
        q: "Can Shipmaster grow with me as I scale beyond 50k orders/month?",
        a: "Honest answer: at very large enterprise scale (100k+ orders/mo with omnichannel + B2B + retail distribution), brands often outgrow boutique 3PLs and move to enterprise providers like Ryder. We're transparent about that. We'd rather grow with you to that point and help you transition cleanly than pretend we're the right fit forever.",
      },
    ],
  },
];

export const COMPETITORS_BY_SLUG = Object.fromEntries(
  COMPETITORS.map((c) => [c.slug, c]),
);

export const ALL_COMPETITOR_SLUGS = COMPETITORS.map((c) => c.slug);
